// Pure helpers for the SMARTDent Standalone voucher channel. Everything in
// this module is network-free and side-effect-free so it can be unit-tested
// without a DynamoDB / gateway mock. See also ./smartDentQueries.js for the
// DoDB-bound primitives and ../actions/SmartDentAction.js for the thunks
// that compose these pieces.

import { resolveEffectiveOwner, getDirectAllocationChildren, computeNodeQuota } from './allocationGraph';
import { extractDealerIdFromSkStrict } from './licenseHelper';
import { isCorpLedgerSk, isReceivedAllocationSk } from './rayteamsTables';

// ─── Limits / patterns ───────────────────────────────────────────────────────

export const MAX_QR_PER_REQUEST = 100;
export const MAX_REASON_LENGTH = 500;
export const MAX_VALIDUNTIL_HORIZON_MS = 1000 * 60 * 60 * 24 * 365 * 10; // 10 years
export const ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

// ─── Input validation ────────────────────────────────────────────────────────

export const isValidId = (id) => typeof id === 'string' && ID_PATTERN.test(id);

export const isValidLcuseSk = (sk) =>
    typeof sk === 'string' && sk.startsWith('lcuse:');

export const isValidValiduntil = (v) => {
    if (!Number.isInteger(v)) return false;
    const now = Date.now();
    return v > now && v <= now + MAX_VALIDUNTIL_HORIZON_MS;
};

export const sanitizeReason = (reason) => {
    if (reason == null) return null;
    if (typeof reason !== 'string') return null;
    return reason.slice(0, MAX_REASON_LENGTH);
};

// ─── Quota math ──────────────────────────────────────────────────────────────

// PR #217 Round 1 (#5): computeCorpRemaining (t-u-sum) 제거.
// 사유: UI 사용처 0개 (BUG-105 로 computeCorpUnallocated 로 전환), dead code 보존이
// "Allocation Summary 용 잔여 함수" 검색 시 잘못 재선택될 BUG 재발 함정. git history 로
// 언제든 복구 가능.

/**
 * Corp(본사) 레벨 표시용 미할당량 — `allocatable = t_* − Σ(dealer.t_*)`.
 * IMP-156: computeCorpUnallocated + computeCorpIssuable 통합(BUG-105 회귀 방지 — 비슷한 두
 * 함수 중 잘못 선택 방지).
 *
 * ⚠️ 결정(할당 상한·발급 게이트)에는 쓰지 않는다. 리딤(`u`)을 차감하지 않아 이미 쓴 몫까지
 * 배분 가능으로 보인다. 결정은 `resolveSelfUsableCeiling`(Self-Usable = t − 자기소모 −
 * Σ직속자식.t) SSoT 만 쓴다(IMP-427).
 *
 * ⚠️ BUG-206 (#369, 사용자 확정 2026-07-31): 종전 `.issuable`(t − i − Σd) 필드는 **제거**했다.
 * `i`(발행 바우처 수)는 리딤 전까지 무한 재발행 가능한 참고치여서 quota 를 결정할 수 없고
 * (t=1 이어도 i 는 무한히 올라간다), 남겨두면 "발급 가능량" 으로 오선택되는 함정이 된다.
 *
 * 소비처: LicenseOverviewTab / LicenseSubsidiaryTab 표시 Remaining 의 legacy 폴백(최상위).
 *
 * ⚠️ 남은 범위 — IMP-428(#372): 그 legacy 폴백(노드 미도착·`GetAllocationSubtree` 실패·자기 노드
 * 미발견)은 최상위에서만 여전히 `allocatable`(u 미차감)이라 집행 천장(`resolveSelfUsableCeiling`)보다
 * 크게 표시된다 — 조회 실패 시 지속 노출된다. 최상위 예외의 근거였던 BUG-191(#332) "원장 u 롤업"
 * 전제는 BUG-206(#369)에서 롤업 되빼기로 해소됐으므로, 표시 통일은 IMP-428 에서 수행한다
 * (본 PR 범위는 발급 천장). 통일 후 이 주석과 함수 자체를 제거 검토.
 */
export const computeCorpQuotaBreakdown = (corpRecord, dealerRecords = []) => {
    if (!corpRecord) {
        return { allocatable: { newQty: 0, renewQty: 0 } };
    }
    const sum = sumDealerTotals(dealerRecords);
    const tInitial = corpRecord.t_initial ?? 0;
    const tRenewal = corpRecord.t_renewal ?? 0;
    return {
        allocatable: {
            newQty: Math.max(0, tInitial - sum.newQty),
            renewQty: Math.max(0, tRenewal - sum.renewQty),
        },
    };
};

/**
 * FEAT-050 S4: N단계 quota 진입점. 임의 회사(companyId)의 effective owner 를 해석하고
 * 그 노드의 직속 explicit 자식 명시할당을 반영해 quota breakdown 을 반환한다.
 * 기존 2-tier 함수(computeCorpQuotaBreakdown/computeDealerRemaining)는 S5 소비처 전환까지 유지.
 * allocationGraph(S3) 조합. 순수 함수(DDB 접근 없음).
 */
export const computeNodeQuotaBreakdown = (companyId, ctx = {}) => {
    const { nodesByCompanyId, parentOf } = ctx;
    const owner = resolveEffectiveOwner(companyId, { nodesByCompanyId, parentOf });
    if (!owner) {
        // IMP-420 m5: 성공 shape(computeNodeQuota)과 대칭 — selfUsable 누락 시 소비처 undefined 방지.
        const zero = { allocatable: 0, remaining: 0, revokable: 0, selfUsable: 0 };
        return { effectiveOwner: null, initial: { ...zero }, renewal: { ...zero } };
    }
    const ownerNode = nodesByCompanyId[owner];
    const children = getDirectAllocationChildren(owner, nodesByCompanyId);
    return { effectiveOwner: owner, ...computeNodeQuota(ownerNode, children) };
};

// IMP-427 (#368): Allocation Summary 배지용 원천 수치 추출. 파생 quota(잔여/발급가능)를
// 계산하지 않고 레코드의 t/u/i 와 직속 자식 Σt 를 그대로 꺼내는 순수 추출기다 — 표시 전용.
//
// 반환 `null` = 레코드 미도착. 호출처가 배지 자체를 렌더하지 않도록 하기 위한 것으로,
// 0 을 채워 반환하면 조회 전 상태가 "전부 0" 으로 보여 오해를 준다.
//
// `allocated`(Σ직속자식.t)는 항상 계산해 담되, 하위 조직이 없는 딜러 화면은 호출처가
// 이 필드를 전달하지 않는다(배지 생략).
//
// 발급 누적(`i`)은 담지 않는다 — 사용자 확정(2026-07-31): `i > t` 가 정상(마케팅 초과 발급)이라
// 뺄셈 항목이 될 수 없고, 잔여 수치 옆에 노출하면 혼선만 유발한다. 용어 SSoT 는
// prd-rayguard/docs/specs/quota-glossary.md.
export const extractAllocationFacts = (record, directChildren = []) => {
    if (!record) return null;
    const children = Array.isArray(directChildren) ? directChildren : [];
    const sumChildT = (suffix) => children.reduce((sum, c) => sum + ((c && c[`t_${suffix}`]) || 0), 0);
    return {
        total: { newQty: record.t_initial ?? 0, renewQty: record.t_renewal ?? 0 },
        allocated: { newQty: sumChildT('initial'), renewQty: sumChildT('renewal') },
        redeemed: { newQty: record.u_initial ?? 0, renewQty: record.u_renewal ?? 0 },
    };
};

// IMP-427 (#368, 사용자 확정 2026-07-31): **가용량 SSoT.** 표시(Allocation Summary) · 할당 팝업
// 상한 · 할당/발급 집행 게이트가 모두 이 함수를 거친다. 종전에는 같은 "가용량"을 최상위는
// 발행 수 기준 `t−i−Σd`(BUG-206 으로 폐기) / `allocatable`(t−Σd), 중간법인은
// `selfUsable`(t−u−Σd) 로 계산해 화면과 서버가 어긋났다(팝업 5 입력 → 게이트 3 으로 거절).
//
// 공식 = Self-Usable `t − 자기소모 − Σ직속자식.t`. `i`(발급 누적)는 참고 값이며 계산에 쓰지
// 않는다 — 마케팅 초과 발급 허용 모델이라 `i > t` 가 정상이고, 예산 인지 redeem(예산 없으면
// 리딤 실패)이 외부에서 강제되므로 미리딤 바우처(i−u)는 over-commit 을 일으키지 않는다.
//
// [자기소모 산출 — prod 실데이터로 확정, 2026-07-31]
// 사용자가 제공한 prod 레코드(mg `testtest-9371-…`)로 두 형태의 `u` 의미를 확정했다:
//   원장 info:{mg}                t=10 u=5
//   ├ info:{mg}:RAY99901(자기딜)        u=1
//   ├ info:{mg}:RAY99902  t=7          u=1   allocatedBy=RAY99901
//   └ info:{mg}:RAY99903  t=6          u=3   allocatedBy=RAY99902
//  · 원장 u(5) == 1+1+3 → **원장은 mg 그룹 전체 롤업**(사용자 확정: "자식들과 그 자식들의
//    자식들… 즉 mg 그룹 전체"). 자기소모를 얻으려면 그룹 내 전 3-seg 리딤(4)을 빼야 한다(1).
//    직속 자식만 빼면(1) 손자 리딤 3 이 남아 자기소모가 과대해진다.
//    ⚠️ "서브트리"가 아니라 **mg 그룹** 기준이다 — mg 를 공유하는 조직이 있으면 두 범위가 다르다
//    (BUG-198/IMP-420 의 shared-mg 토폴로지). `ctx.allRows` 로 mg 전체 목록을 받는 이유.
//  · RAY99902 는 u=1 인데 그 자식 RAY99903 이 3 을 썼다 → 롤업이면 최소 3 이어야 하므로
//    **3-seg 받은 레코드의 u 는 자기 것만**이다. 보정하지 않는다.
// 검산: RAY99902 = 7−1−6 = 0, RAY99901 = 10−(5−4)−7 = 2 — 둘 다 화면 표시값과 일치.
//
// 상반 가정(3-seg 도 롤업)은 커밋 `38142880` 에서 해소 — `IssueQr` corp-direct 분기에 동일
// `isCorpLedgerSk` 게이트를 적용하고 `SmartDentActionIssueQrCorpDirectSelfUsable` 중간법인 케이스를
// 확정 규칙(2)으로 갱신했다. `SmartDentAction.js` pool-participant 분기의 옛 공식(`t−i−Σd`)도
// BUG-206(#369) 에서 본 함수로 통일하고 필드 자체를 제거했다.
//
// [모집단] Σ직속자식.t 와 `u` 보정은 **반드시 같은 집합**을 써야 한다. PR #540 리뷰 Major:
// 종전 구현은 `u` 보정만 strict(`allocatedBy === companyId`)로 하고 Σt 는 미필터 원본을 넘겨
// (`computeNodeQuota(node, rows)`) 두 집합이 갈렸다.
//
// 그런데 양쪽을 strict 로 맞추면 **`allocatedBy` 부재 레거시 직속 행이 Σt 에서 빠져 천장이 과대**가
// 된다(과잉 할당 허용 = 위험한 방향. IMP-420 계약상 최상위에서 부재 행은 정상 직속 자식이다).
// 따라서 스코프 판정은 호출처의 `scopeOwnDealers`(tier 별 규약: 최상위=부재 행 포함 / 중간법인=strict)
// 에 위임하고, 이 함수는 전달받은 집합에서 **셀프딜링 행만 제외**해 Σt·`u` 보정에 동일 적용한다.
// 셀프딜링 행을 자식으로 세면 자기 몫이 Σt 로 이중 차감된다(BUG-200 계약).
//
// `ctx.nodesByCompanyId` 가 주어지면 **노드 모델을 우선**한다. 표시·팝업·집행 게이트가 같은 함수
// 한 곳을 지나게 하기 위한 것으로, 종전에는 팝업만 노드 우선 + 게이트는 항상 레코드 경로여서 두
// 모집단이 갈렸다(모달 > 게이트 → "팝업이 허용한 수량을 서버가 거절" 재발 경로).
// 노드 경로는 `buildNodesByCompanyId` 가 원장 롤업 `u` 를 이미 처리했으므로 추가 보정하지 않는다.
// 불변식 위반 warn dedup — `(companyId, sk, field)` 조합별 1회만 남긴다.
const warnedNegativeSelfConsumedKeys = new Set();
// 테스트 간 격리용 — 모듈 레벨 Set 은 suite 를 넘어 살아남아 warn 단언이 서로를 가린다.
export const _clearNegativeSelfConsumedWarnCacheForTest = () => warnedNegativeSelfConsumedKeys.clear();

// BUG-206 R3 (PR #543 리뷰 Major): **원장 자기소모 단일 구현.** 표시(노드 조립,
// `allocationContext.buildNodesByCompanyId`)와 집행(레코드 경로, 아래 `resolveSelfUsableCeiling`)이
// 같은 식을 각자 구현하면 다음 판정 변경 때 한쪽만 갱신된다 — 사용자 지침 "표시용/집행용 계산 분리
// 금지". 두 경로 모두 이 함수를 호출한다.
//
//   자기소모 = max( 원장u − Σ(같은 mg 다른 회사 3-seg 행.u),  Σ(자기 셀프딜 행.u) )
//
// · 앞 항 = mg 그룹 롤업 되빼기(prod 실데이터 확정 — quota-glossary §1.1).
// · 음수(불변식 위반)면 보정 없는 원장 `u` 로 폴백하고 **warn**(무음 보정 금지, CLAUDE.md #7).
//   0 클램프는 자기소모를 통째로 무시해 천장이 `t − Σ자식.t` 까지 오르는 fail-open 이었다.
// · 뒤 항 = 레거시 셀프딜 행 보호. 원장 `u` 가 셀프딜 리딤을 롤업하지 **않은** 데이터에서 되빼기만
//   쓰면 자기소모가 과소 → 천장 과대(over-issuance)가 된다.
export const computeLedgerSelfConsumed = ({ ledgerU = 0, othersU = 0, selfDealU = 0, warnCtx = {} }) => {
    const raw = ledgerU - othersU;
    if (raw < 0) {
        // dedup — 게이트는 요청마다, 표시·팝업은 렌더마다 호출하므로 불변식이 깨진 mg 하나로
        // 경고가 폭주한다. `(호출처, companyId, sk, field)` 조합별 1회.
        //
        // R4 리뷰 Minor: 키에 `helper` 를 포함해야 한다 — 렌더마다 도는 표시 경로가 키를 선점하면
        // "같은 위반이 실제 발급/할당 게이트를 태웠다" 는 사실이 로그에 남지 않아 통합 전보다 집행
        // 관측성이 좁아진다(`SmartDentAction` 의 `|ledger` 와 같은 클래스).
        const dedupKey = `${warnCtx.helper ?? '-'}|${warnCtx.companyId ?? '-'}|${warnCtx.recordSk ?? '-'}|${warnCtx.field ?? '-'}`;
        if (!warnedNegativeSelfConsumedKeys.has(dedupKey)) {
            warnedNegativeSelfConsumedKeys.add(dedupKey);
            console.warn('[quota] computeLedgerSelfConsumed: 자기소모 음수 — 원장 u 롤업 불변식 위반 의심', {
                helper: warnCtx.helper ?? 'computeLedgerSelfConsumed',
                companyId: warnCtx.companyId,
                field: warnCtx.field,
                recordSk: warnCtx.recordSk,
                recordU: ledgerU,
                sumRollupU: othersU,
                reason: '원장 u < Σ(mg 그룹 3-seg u) — 보정 없는 원장 u 로 폴백',
            });
        }
    }
    return Math.max(raw < 0 ? ledgerU : raw, selfDealU);
};

export const resolveSelfUsableCeiling = (record, dealerRecords, companyId, ctx = {}) => {
    const { nodesByCompanyId, parentOf, allRows } = ctx;
    if (nodesByCompanyId && Object.keys(nodesByCompanyId).length > 0) {
        const bd = computeNodeQuotaBreakdown(companyId, { nodesByCompanyId, parentOf });
        if (bd.effectiveOwner) {
            return { newQty: bd.initial.selfUsable, renewQty: bd.renewal.selfUsable };
        }
    }
    if (!record) return { newQty: 0, renewQty: 0 };
    const rows = Array.isArray(dealerRecords) ? dealerRecords : [];
    // BUG-206 R3 (PR #543 리뷰 Major #2): 셀프딜 판정도 3-seg **양성** 판정(strict) 하나로 고정한다.
    // 종전 non-strict `extractDealerIdFromSk`(`parts[2] || sk`)는 `info::{id}`·4-seg 에서 노드 경로의
    // `isReceivedAllocationSk` 와 경계가 달라 같은 mg 에서 두 자기소모가 갈렸다.
    const notSelfDeal = (d) => !companyId || extractDealerIdFromSkStrict(d?.sk) !== companyId;
    const direct = rows.filter((d) => d && notSelfDeal(d));
    // 원장(2-seg)의 `u` 는 **mg 그룹 전체** 롤업이므로 자기소모를 얻으려면 그룹 내 전 3-seg 리딤을
    // 빼야 한다 — 직속 자식만 빼면 손자 리딤이 남아 자기소모가 과대 산정된다(실측 검증 위 주석).
    // allRows 미전달 시 direct 로 폴백(호출처가 mg 전체 목록을 못 주는 경우).
    // 모집단은 **3-seg 행만** — 노드 경로(build3SegURollup)와 같은 경계다.
    const rollupSource = (Array.isArray(allRows) ? allRows : rows).filter((d) => d && isReceivedAllocationSk(d.sk));
    const rollupRows = rollupSource.filter(notSelfDeal);
    const sumRollupU = (field) => rollupRows.reduce((acc, d) => acc + (d?.[field] ?? 0), 0);
    // 레거시 셀프딜 행 u 합 — 자기소모 하한(computeLedgerSelfConsumed 주석 참조).
    const selfDealRows = rollupSource.filter((d) => !notSelfDeal(d));
    const sumSelfDealU = (field) => selfDealRows.reduce((acc, d) => acc + (d?.[field] ?? 0), 0);
    // 자기소모 산출은 표시 경로와 **공유 함수** 하나를 쓴다(BUG-206 R3 리뷰 Major).
    const selfConsumed = (field) => computeLedgerSelfConsumed({
        ledgerU: record[field] ?? 0,
        othersU: sumRollupU(field),
        selfDealU: sumSelfDealU(field),
        warnCtx: { helper: 'resolveSelfUsableCeiling', companyId, field, recordSk: record.sk },
    });
    const node = isCorpLedgerSk(record.sk)
        ? {
            ...record,
            u_initial: selfConsumed('u_initial'),
            u_renewal: selfConsumed('u_renewal'),
        }
        : record;
    const q = computeNodeQuota(node, direct);
    return { newQty: q.initial.selfUsable, renewQty: q.renewal.selfUsable };
};

// IMP-427 (#368, PR #540 리뷰 M5): Allocation Summary 배지 조립 SSoT. 종전에는 배지 3키 배열이
// AllocationSummaryCard 와 LicenseSubsidiaryTab(인라인 카드) 양쪽에 복제돼 이미 드리프트가
// 발생했다(리딤 노출 게이트가 법인 탭에만 존재).
//
// `includeRedeemed` = 표시 remaining 이 실제로 그 `u` 를 차감하는가. false 면 리딤 배지를 뺀다 —
// 차감하지 않는 값을 배지에 두면 운영자가 `Total − Allocated − Redeemed` 로 잘못 뺄셈한다.
// `includeAllocated` = 하위 배분 배지를 노출할지(하위 조직이 없는 딜러 자기-뷰는 false).
export const buildSummaryBadges = (facts, { includeRedeemed = true, includeAllocated = true } = {}) => [
    { key: 'total', labelKey: 'common.label.total', qty: facts?.total },
    { key: 'allocated', labelKey: 'license.status.allocated', qty: includeAllocated ? facts?.allocated : null },
    { key: 'redeemed', labelKey: 'license.label.redeemed', qty: includeRedeemed ? facts?.redeemed : null },
].filter((b) => b.qty);

// Dealer remaining pool — display-oriented. Formula: `t_* - u_*`.
// SMARTDent 라이선스 비즈니스 모델 (사용자 확정):
// - t_* = 활성화(redeem) budget — 이만큼만 라이선스 활성화 가능
// - i_* = 발급된 voucher 수 — 마케팅 차원으로 t 초과 발급 가능 (over-issuance 허용)
// - u_* = 실제 redeem 된 수
// - 실제 불변식: u ≤ t (i ≤ t 는 NOT 강제 — license-api.md SSoT 와 차이, 운영 우선)
//
// 따라서:
// - Remaining = t - u : 활성화 가능한 잔여 budget. 음수 불가 (u≤t 보장).
// - Revokable = t - u : 회수해도 안전한 양 (이미 redeem 된 라이선스는 회수 불가).
// (BUG-206 #369: `Issuable = t − i` 항은 제거했다 — i 는 리딤 전까지 무한 재발행되는 참고치라
//  quota 를 결정할 수 없다. 공식으로 남겨두면 "발급 가능량" 으로 재오선택된다.)
export const computeDealerRemaining = (dealerRecord) => {
    if (!dealerRecord) return { newQty: 0, renewQty: 0 };
    return {
        newQty: Math.max(0, (dealerRecord.t_initial ?? 0) - (dealerRecord.u_initial ?? 0)),
        renewQty: Math.max(0, (dealerRecord.t_renewal ?? 0) - (dealerRecord.u_renewal ?? 0)),
    };
};

// BUG-206 (#369, 사용자 확정 2026-07-31): `computeDealerIssuable`(t − i) **제거**.
// 프로덕션 소비처 0건(딜러 발급 게이트는 `computeDealerRemaining`= t − u)이었고, `i` 는 리딤
// 전까지 무한 재발행되는 참고치라 quota 를 결정할 수 없다. 회수 가능량은
// `computeDealerRevokable`(아래) 를 쓴다.

// Revoke UI 전용 회수 가능량 — over-issuance 모델 반영. Voucher 발급(i)은 마케팅 차원
// over-print 허용이므로 회수 제약은 i 가 아닌 u (이미 redeem 된 라이선스). = t - u.
// 음수 불가 (u≤t budget 보장). AllocateDealerModal Revoke 모드 max 입력 상한으로 사용.
//
// PR #191 review (Code Quality / Architecture Major) — 현재 본문이 computeDealerRemaining
// 과 동일(t-u). 두 함수의 의미적 의도는 다르다 (잔여 budget vs 회수 가능량) 의도적으로
// 별개로 유지하되 hidden coupling 위험을 명시. 향후 over-issuance 비즈니스 룰이 한쪽만
// 변경되면 함수 본문 분기 가능 — 그 시점에 alias 분리.
export const computeDealerRevokable = computeDealerRemaining;

// PR #217 Round 1 (#2): dealerRecords 가 null 일 때 (미초기화 Redux state 등)
// .reduce 가 TypeError 발생하지 않도록 Array.isArray 가드. computeCorpQuotaBreakdown
// 호출자 보호.
const sumDealerTotals = (dealerRecords) => {
    if (!Array.isArray(dealerRecords)) return { newQty: 0, renewQty: 0 };
    return dealerRecords.reduce(
        (acc, d) => ({
            newQty: acc.newQty + (d?.t_initial ?? 0),
            renewQty: acc.renewQty + (d?.t_renewal ?? 0),
        }),
        { newQty: 0, renewQty: 0 },
    );
};

// FEAT-050 BUG-177: 중간 법인은 자기 corp 원장이 비고 예산이 상위에게서 받은 레코드
// (info:{부모mgr}:{self})에 있음. received-first 로 자기 예산을 SSoT 로 해석한다.
// sk 가 `:${companyId}` 로 끝나는(자기 소유) 받은 레코드만 채택(공유 슬롯 오염 가드).
// BUG-191: sk 가 :companyId 로 끝나도 allocatedBy===companyId 면 self-dealing(자기 자신에게
// 할당해 딜러 역할 수행)이다. self-deal 은 "상위에게서 받은 예산(sub-entity)"이 아니라 자기 corp
// 원장을 예산으로 써야 한다(resolveScopeContext 의 allocatedBy!==corpId 판정과 정합). self-deal
// 슬롯을 예산으로 오인하면 그 값이 Σd 에서 또 차감돼 잔여가 0 으로 오표시된다.
// IMP-420(#354) 문제 1: scopeContextOverride(5번째, optional) — resolveScopeContext(thunk SSoT)
// 의 isSubEntity 판정을 채택해 본 함수의 자체 추정(sk suffix + allocatedBy)을 대체한다.
// thunk 가 판정 SSoT 이므로 override 가 이긴다. companyId 일치를 먼저 검증해 다른 회사
// 컨텍스트가 흘러들어와 오염되는 것을 차단하고, isSubEntity 가 boolean 이 아니면(형식 이상)
// 신뢰하지 않는다. 미채택(override 없음/불일치/비-boolean)이면 기존 자체 판정 그대로 유지
// (무회귀).
// 사전 리뷰 Major M1: 판정(isSubEntity)과 예산 원천(effectiveCorp)은 별개 문제다. override 로
// isSubEntity===false 를 채택할 때 myReceivedAllocation 을 아예 버리면, 자기 mg 를 보유하면서
// 부모의 다른 mg 네임스페이스에서 예산을 받은 "하이브리드" 회사(resolveScopeContext 는 자기 mg
// 내부만 보므로 false 를 준다)에서 corpAllocation 이 비면 예산이 0 이 되어 AllocateDealerModal
// 수량 입력이 max=0 으로 잠긴다. 따라서 판정은 override 를 따르되 예산은 "있는 것 중 최선"으로
// 폴백한다(corpAllocation 우선 — 최상위 판정이므로 — 없으면 받은 레코드).
// 판정이 자체 추정과 갈리면 warn 으로 관측 가능하게 남긴다(무음 덮어쓰기 금지, CLAUDE.md #7 정신).
// PR #538 리뷰 Minor(divergence warn dedup): 컴포넌트 렌더마다 재호출되면 동일 divergence 가
// 반복 warn 되어 로그가 폭주한다. 동일 (companyId, overrideIsSubEntity, selfDetectedIsSubEntity)
// 조합은 프로세스 생애주기 동안 1회만 warn — 관측성은 유지하되 폭주만 억제(테스트 전용 리셋 API
// 는 두지 않는다. 테스트는 케이스별 전용 companyId 사용으로 격리).
const warnedDivergenceKeys = new Set();

export const resolveEffectiveCorpBudget = (
    corpAllocation,
    myReceivedAllocation,
    companyId,
    licensesFallback = null,
    scopeContextOverride = null,
) => {
    const ownReceived = (myReceivedAllocation?.sk?.endsWith(`:${companyId}`)
        && myReceivedAllocation.allocatedBy !== companyId) ? myReceivedAllocation : null;
    // Minor(companyId falsy): 둘 다 undefined 면 오탐 채택 방지.
    const overrideAdopted = !!companyId && scopeContextOverride?.companyId === companyId
        && typeof scopeContextOverride?.isSubEntity === 'boolean';
    if (overrideAdopted) {
        const isSubEntity = scopeContextOverride.isSubEntity;
        const effectiveCorp = isSubEntity
            ? (myReceivedAllocation || corpAllocation || licensesFallback)
            : (corpAllocation || myReceivedAllocation || licensesFallback);
        if (isSubEntity !== !!ownReceived) {
            const budgetSource = effectiveCorp === corpAllocation ? 'corpAllocation'
                : effectiveCorp === myReceivedAllocation ? 'myReceivedAllocation'
                    : effectiveCorp === licensesFallback && licensesFallback ? 'licensesFallback' : 'none';
            const dedupKey = `${companyId}|${isSubEntity}|${!!ownReceived}`;
            if (!warnedDivergenceKeys.has(dedupKey)) {
                warnedDivergenceKeys.add(dedupKey);
                console.warn('[resolveEffectiveCorpBudget] scope judgment divergence — thunk override adopted', {
                    companyId,
                    overrideIsSubEntity: isSubEntity,
                    selfDetectedIsSubEntity: !!ownReceived,
                    budgetSource,
                });
            }
        }
        return { effectiveCorp, isSubEntity };
    }
    return { effectiveCorp: ownReceived || corpAllocation || licensesFallback, isSubEntity: !!ownReceived };
};

// 중간 법인(isSubEntity)은 부모 mgr 스코프에 형제/부모 딜러가 섞이므로 자기가 배분한 것
// (allocatedBy===companyId)만. 귀속불명(allocatedBy 없음)은 형제일 수 있어 제외.
//
// IMP-420(#354) 문제 2: 최상위도 직속만 남긴다(종전 무필터 passthrough 폐지). mg-null 중간법인이
// 자기 자식을 부모 mg 네임스페이스(info:{부모mg}:{손자})에 쓰는 3단계 조직에서는 최상위의
// queryDealerAllocations(부모mg) 결과에 손자까지 섞여 Σ(dealer.t) 이중차감이 발생했다(실측:
// 원장 t=10 / 직속 5 / 손자 2 → Σd 에 손자가 더해져 잔여가 실제보다 작게 표시·게이트됨).
// 중간법인 분기는 이미 직속 필터라 문제 없었어 최상위/중간 비대칭이었다.
// 부재(allocatedBy 없음) 처리만 tier 별로 다르다 — 최상위는 자기 mg 소유자라 부재 행이 자기
// 배분일 개연성이 높아 보수적으로 Σ 에 남기고(과다부여 방지), 중간법인은 부모 mg 공유라 부재
// 행이 형제일 수 있어 제외한다.
export const scopeOwnDealers = (dealerAllocations, companyId, isSubEntity) => {
    const arr = Array.isArray(dealerAllocations) ? dealerAllocations : [];
    // companyId 부재 시 필터 불가 — 원본 유지(Σ 를 줄이면 과다부여 방향으로 샌다).
    if (!companyId) return arr;
    return isSubEntity
        ? arr.filter((d) => d && d.allocatedBy === companyId)
        : arr.filter((d) => d && (d.allocatedBy === companyId || d.allocatedBy == null));
};

// BUG-180: 중간 법인은 자기 manager-group 으로 해석 안 될 수 있다. 받은 레코드
// (info:{parentMg}:{self}) 의 sk 에서 parentMg 를 도출한다. self(3번째 세그먼트)가 companyId
// 와 일치할 때만 채택(cross-tenant 방어 — 리뷰 Minor). 형식 이상/불일치 시 null(fail-closed).
export const deriveMgrIdFromReceivedSk = (sk, companyId) => {
    if (!sk || !companyId) return null;
    const parts = String(sk).split(':');
    if (parts.length < 3 || parts[2] !== companyId) return null;
    return parts[1] || null;
};

// ─── Customer license filters / history ─────────────────────────────────────

export const filterLicensesByStatus = (licenses, status) => {
    const now = Date.now();
    switch (status) {
        case 'active':
            return licenses.filter((l) => !l.suspended && (l.expired ?? 0) > now);
        case 'suspended':
            return licenses.filter((l) => l.suspended === true);
        case 'expired':
            return licenses.filter((l) => (l.expired ?? 0) <= now);
        case 'all':
        default:
            return licenses;
    }
};

export const buildHistoryEvent = (action, { at, reason, actor }) => ({
    action,
    at,
    by: actor?.id ?? null,
    actorType: actor?.type ?? null,
    reason: sanitizeReason(reason),
});

// ─── QR token generator ──────────────────────────────────────────────────────

// Per license-api.md "QR 보안": Base62, CSPRNG-backed, >= 128-bit entropy.
// 22 Base62 chars give ~131 bits. Rejection sampling avoids modulo bias
// (256 mod 62 != 0, so naive `byte % 62` over-represents chars 0-7).
const BASE62_CHARS =
    '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
export const QR_TOKEN_LENGTH = 22;
const BYTE_REJECT_CUTOFF = Math.floor(256 / 62) * 62; // 248

export const generateQrToken = () => {
    const rng = globalThis.crypto;
    if (!rng || typeof rng.getRandomValues !== 'function') {
        throw new Error('CSPRNG unavailable; refusing to generate QR token');
    }
    const chars = [];
    let safety = 16; // each iteration yields >= length * 0.969 usable bytes
    while (chars.length < QR_TOKEN_LENGTH) {
        if (safety-- <= 0) {
            throw new Error('QR token RNG stalled beyond expected bounds');
        }
        const buf = new Uint8Array(QR_TOKEN_LENGTH * 2);
        rng.getRandomValues(buf);
        for (let i = 0; i < buf.length && chars.length < QR_TOKEN_LENGTH; i += 1) {
            if (buf[i] < BYTE_REJECT_CUTOFF) {
                chars.push(BASE62_CHARS[buf[i] % 62]);
            }
        }
    }
    return chars.join('');
};

// ─── Optimistic-lock condition builders (BUG-165) ───────────────────────────

// DynamoDB: 부재 속성은 0과 다르게 취급된다(부재 ≠ 0). 낙관적 잠금 기대값이 0이면
// 속성을 초기화한 적 없는(부재) 레코드도 잠금을 통과해야 한다. 기대값이 0일 때만
// 부재를 허용하고, 0이 아니면(속성 존재) 엄격 비교를 유지해 실제 동시 변경은 계속 탐지.
// 전제: expected 는 호출부에서 `?? 0` 로 정규화된 number (=== 0 비교, undefined 아님).
export const lockEq = (attr, ref, expected) =>
    expected === 0 ? `(${attr} = ${ref} OR attribute_not_exists(${attr}))` : `${attr} = ${ref}`;

// revoke 의 하한 가드(>=)에 동일한 부재≡0 시맨틱 적용.
// 전제: amount 는 호출부에서 `?? 0` 로 정규화된 number (=== 0 비교, undefined 아님).
export const lockGte = (attr, ref, amount) =>
    amount === 0 ? `(${attr} >= ${ref} OR attribute_not_exists(${attr}))` : `${attr} >= ${ref}`;

// ─── DoDB result classifiers ─────────────────────────────────────────────────

// DoDB returns [] on any gateway error. For update calls an empty array is
// always a failure signal (a successful update never returns an array).
export const isUpdateFailure = (result) => {
    if (result == null) return true;
    if (Array.isArray(result) && result.length === 0) return true;
    return false;
};

// Extract the updated record from an `update` response. Handles both the
// { Attributes: {...} } envelope and the bare record shape the gateway
// sometimes returns.
export const normalizeUpdateResult = (result) => {
    if (!result || Array.isArray(result)) return null;
    if (result.Attributes && typeof result.Attributes === 'object') {
        return result.Attributes;
    }
    if (result._id && result.sk) return result;
    return null;
};
